import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import { Search, ChevronDown, Download, X, Filter, Sprout, Coins } from "lucide-react";
import { ListTemplate } from "@/templates/ListTemplate";
import { Table } from "@/components/Table";
import { TablePager } from "@/components/TablePager";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Menu } from "@/components/Menu";
import { EmptyState } from "@/components/EmptyState";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/money";

interface Row {
  id: string;
  contributedAt: string | null;
  amountMinor: number;
  allocatedMinor: number;
  currency: string;
  status: string;
  entryType: string;
  destination: string | null;
  allocationsCount: number;
}

interface Props {
  contributions: {
    rows: Row[];
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
    perPageOptions: number[];
  };
  query: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  projects: string[];
  availableProjects: { id: string; name: string }[];
}

const STATUSES = ["all", "submitted", "allocated", "confirmed", "settled", "reversed"];

const money = (minor: number, currency = "GBP") => formatMoney(minor / 100, currency);

function fmtWhen(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function ContributionsIndex(props: Props) {
  const { contributions, status, startDate, endDate, projects, availableProjects } = props;
  const [q, setQ] = useState(props.query);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const firstRender = useRef(true);

  function reload(next: Record<string, string | string[]>) {
    const merged: Record<string, string | string[]> = {
      q,
      status,
      start_date: startDate ?? "",
      end_date: endDate ?? "",
      projects,
      ...next,
    };
    const data: Record<string, string | string[]> = {};
    for (const [k, v] of Object.entries(merged)) {
      if (Array.isArray(v)) {
        if (v.length) data[k] = v;
      } else if (v && !(k === "status" && v === "all")) {
        data[k] = v;
      }
    }
    router.get("/contributions", data, {
      only: ["contributions", "query", "status", "startDate", "endDate", "projects"],
      preserveState: true,
      preserveScroll: true,
      replace: true,
    });
  }

  // Debounce the free-text search.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const t = setTimeout(() => reload({ q }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const projectName = (id: string) => availableProjects.find((p) => p.id === id)?.name ?? id;
  const toggleProject = (id: string) => {
    const has = projects.includes(id);
    reload({ projects: has ? projects.filter((p) => p !== id) : [...projects, id] });
  };

  // Active-filter chips (each clears its own filter).
  const chips: { key: string; label: string; onClear: () => void }[] = [];
  if (status && status !== "all")
    chips.push({ key: "status", label: `Status: ${status}`, onClear: () => reload({ status: "all" }) });
  projects.forEach((id) =>
    chips.push({
      key: `project:${id}`,
      label: `Project: ${projectName(id)}`,
      onClear: () => reload({ projects: projects.filter((p) => p !== id) }),
    }),
  );
  if (startDate) chips.push({ key: "from", label: `From ${startDate}`, onClear: () => reload({ start_date: "" }) });
  if (endDate) chips.push({ key: "to", label: `To ${endDate}`, onClear: () => reload({ end_date: "" }) });
  if (q) chips.push({ key: "q", label: `“${q}”`, onClear: () => setQ("") });

  const activeCount = chips.length;

  function clearAll() {
    setQ("");
    reload({ status: "all", start_date: "", end_date: "", projects: [], q: "" });
  }

  const projectsLabel = projects.length === 0 ? "All projects" : `${projects.length} selected`;

  return (
    <ListTemplate
      title="Contributions"
      description="Contributions allocated to your projects, newest first."
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Contributions" }]}
      primaryAction={
        <Button asChild intent="secondary" size="md">
          <a href="/contributions.csv">
            <Download /> Export CSV
          </a>
        </Button>
      }
      toolbar={
        <div className="flex w-full flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button intent={filtersOpen ? "secondary" : "ghost"} size="md" onClick={() => setFiltersOpen((o) => !o)}>
              <Filter /> Filters
              {activeCount > 0 && (
                <span className="ml-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-fg-on-accent">
                  {activeCount}
                </span>
              )}
              <ChevronDown className={cn("transition-transform", filtersOpen && "rotate-180")} />
            </Button>

            {chips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.onClear}
                className="inline-flex items-center gap-1 rounded-full bg-accent-subtle px-2 py-1 text-xs text-accent-text transition-colors hover:bg-surface-hover [&_svg]:size-3"
              >
                {chip.label} <X />
              </button>
            ))}

            {activeCount > 0 && (
              <Button intent="ghost" size="sm" onClick={clearAll}>
                Clear all
              </Button>
            )}
          </div>

          {filtersOpen && (
            <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle pt-3">
              <Menu.Root>
                <Menu.Trigger asChild>
                  <Button intent="secondary" size="md">
                    Status: <span className="capitalize">{status}</span>
                    <ChevronDown />
                  </Button>
                </Menu.Trigger>
                <Menu.Content align="start">
                  <Menu.Label>Filter by status</Menu.Label>
                  {STATUSES.map((s) => (
                    <Menu.CheckboxItem key={s} checked={status === s} onCheckedChange={() => reload({ status: s })}>
                      <span className="capitalize">{s}</span>
                    </Menu.CheckboxItem>
                  ))}
                </Menu.Content>
              </Menu.Root>

              {availableProjects.length > 0 && (
                <Menu.Root>
                  <Menu.Trigger asChild>
                    <Button intent="secondary" size="md">
                      <Sprout /> {projectsLabel} <ChevronDown />
                    </Button>
                  </Menu.Trigger>
                  <Menu.Content align="start" className="max-h-80 overflow-auto">
                    <Menu.Label>Filter by project</Menu.Label>
                    {availableProjects.map((p) => (
                      <Menu.CheckboxItem key={p.id} checked={projects.includes(p.id)} onCheckedChange={() => toggleProject(p.id)}>
                        {p.name}
                      </Menu.CheckboxItem>
                    ))}
                  </Menu.Content>
                </Menu.Root>
              )}

              <div className="flex items-center gap-1.5">
                <Input
                  type="date"
                  aria-label="From date"
                  value={startDate ?? ""}
                  onChange={(e) => reload({ start_date: e.target.value })}
                  className="w-[9.5rem]"
                />
                <span className="text-fg-subtle">–</span>
                <Input
                  type="date"
                  aria-label="To date"
                  value={endDate ?? ""}
                  onChange={(e) => reload({ end_date: e.target.value })}
                  className="w-[9.5rem]"
                />
              </div>

              <Input
                leadingIcon={<Search />}
                placeholder="Search id, goal or project…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full sm:w-64"
              />
            </div>
          )}
        </div>
      }
      footer={
        <TablePager
          pagination={{
            page: contributions.page,
            perPage: contributions.perPage,
            total: contributions.total,
            totalPages: contributions.totalPages,
            perPageOptions: contributions.perPageOptions,
            pageParam: "page",
            perPageParam: "per_page",
          }}
          reloadProps={["contributions"]}
          itemLabel={{ singular: "contribution", plural: "contributions" }}
        />
      }
    >
      {contributions.rows.length === 0 ? (
        <EmptyState
          icon={<Coins />}
          title="No contributions found"
          description="No contributions match the current filters."
        />
      ) : (
        <Table>
          <Table.Header>
            <tr>
              <Table.Head className="pl-4">When</Table.Head>
              <Table.Head>Destination</Table.Head>
              <Table.Head className="text-right">Amount</Table.Head>
              <Table.Head className="text-right">Allocated to you</Table.Head>
              <Table.Head>Status</Table.Head>
              <Table.Head>Type</Table.Head>
            </tr>
          </Table.Header>
          <Table.Body>
            {contributions.rows.map((c) => {
              const url = `/contributions/${c.id}`;
              return (
                <Table.Row
                  key={c.id}
                  className="cursor-pointer"
                  onClick={() => router.visit(url)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") router.visit(url);
                  }}
                >
                  <Table.Cell className="pl-4 whitespace-nowrap text-fg-muted tabular">
                    {fmtWhen(c.contributedAt)}
                  </Table.Cell>
                  <Table.Cell className="max-w-[18rem] truncate">
                    {c.destination ?? <Badge tone="warning">Unallocated</Badge>}
                  </Table.Cell>
                  <Table.Cell className="text-right tabular text-fg-muted">
                    {money(c.amountMinor, c.currency)}
                  </Table.Cell>
                  <Table.Cell className="text-right tabular font-medium">
                    {money(c.allocatedMinor, c.currency)}
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge status={c.status} />
                  </Table.Cell>
                  <Table.Cell>
                    {c.entryType === "reversal" ? (
                      <Badge tone="danger">Reversal</Badge>
                    ) : (
                      <span className="text-xs text-fg-muted capitalize">{c.entryType}</span>
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </ListTemplate>
  );
}
